import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { clearAnalyticsCookies, clearLegacyAnalyticsCookies } from './cookies'

export const CONSENT_STORAGE_KEY = 'azoya_consent_v1'

// À incrémenter dès qu'une finalité est ajoutée, retirée, ou change de sens :
// un choix rendu au titre d'une liste de finalités périmée n'est plus un choix
// éclairé, le bandeau doit donc être reposé.
export const CONSENT_VERSION = 2

// La CNIL recommande de ne pas conserver un choix au-delà de six mois.
const CONSENT_MAX_AGE_MONTHS = 6

// `necessary` est vrai par construction : le site ne fonctionne pas sans. Toute
// finalité optionnelle est fausse par défaut — une case pré-cochée est
// interdite, et l'absence de choix doit se comporter exactement comme un refus.
const DEFAULT_PURPOSES = { necessary: true, analytics: false }

const ConsentContext = createContext(null)

function normalizePurposes(purposes) {
  return { ...DEFAULT_PURPOSES, ...purposes, necessary: true }
}

function hasExpired(isoDate) {
  const decidedAt = new Date(isoDate)
  if (Number.isNaN(decidedAt.getTime())) return true
  const expiresAt = new Date(decidedAt)
  expiresAt.setMonth(expiresAt.getMonth() + CONSENT_MAX_AGE_MONTHS)
  return expiresAt.getTime() <= Date.now()
}

// Renvoie le choix enregistré, ou `null` s'il n'y en a pas d'exploitable — et
// c'est bien la même réponse dans tous les cas : jamais choisi, version des
// finalités périmée, choix trop ancien, ou contenu illisible. Un `null` veut
// toujours dire « repose la question », jamais « refuse en silence ».
function readStoredConsent() {
  let raw
  try {
    raw = window.localStorage.getItem(CONSENT_STORAGE_KEY)
  } catch (error) {
    // Navigation privée ou stockage refusé : aucune finalité optionnelle n'est
    // activée, ce qui est le comportement sûr.
    return null
  }
  if (!raw) return null

  let stored
  try {
    stored = JSON.parse(raw)
  } catch (error) {
    return null
  }

  if (!stored || typeof stored !== 'object') return null
  if (stored.version !== CONSENT_VERSION) return null
  if (!stored.purposes || typeof stored.purposes !== 'object') return null
  if (hasExpired(stored.date)) return null

  return {
    version: stored.version,
    date: stored.date,
    purposes: normalizePurposes(stored.purposes),
  }
}

export function ConsentProvider({ children }) {
  const [consent, setStoredConsent] = useState(readStoredConsent)
  const [isPanelOpen, setPanelOpen] = useState(false)
  // Sélection en cours dans le panneau, tant qu'elle n'est pas enregistrée.
  const [draft, setDraft] = useState(() => (consent ? consent.purposes : DEFAULT_PURPOSES))

  // Google Analytics a été retiré du site (#75), mais ses cookies survivent
  // jusqu'à deux ans chez les visiteurs de retour. Plus rien ne les lit : on
  // les efface au premier chargement, sans rapport avec le consentement.
  useEffect(() => {
    clearLegacyAnalyticsCookies()
  }, [])

  const setConsent = useCallback((purposes) => {
    const record = {
      version: CONSENT_VERSION,
      date: new Date().toISOString(),
      purposes: normalizePurposes(purposes),
    }

    // Refus ou retrait de la mesure d'audience : ses traceurs doivent
    // disparaître tout de suite. L'appel est idempotent, donc sûr même quand
    // rien n'avait été déposé — inutile de le conditionner à l'état précédent.
    if (!record.purposes.analytics) clearAnalyticsCookies()

    try {
      window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record))
    } catch (error) {
      // Choix non mémorisé : il vaut pour cette visite et la question sera
      // reposée à la suivante. Préférable à bloquer le visiteur.
      console.error('Choix de cookies non enregistré (stockage indisponible)', error)
    }

    setStoredConsent(record)
    setPanelOpen(false)
  }, [])

  // Le panneau s'ouvre toujours sur le choix réellement enregistré, jamais sur
  // un brouillon laissé par une ouverture précédente refermée sans valider.
  // La recopie est faite ici, dans le même lot de mise à jour que l'ouverture :
  // le premier rendu du panneau affiche donc déjà le bon état. La confier à un
  // effet côté panneau laisserait passer un rendu intermédiaire — donc un
  // clignotement des cases, sur un écran où l'état affiché fait foi.
  const openPanel = useCallback(() => {
    setDraft(consent ? consent.purposes : DEFAULT_PURPOSES)
    setPanelOpen(true)
  }, [consent])

  const closePanel = useCallback(() => setPanelOpen(false), [])

  const setDraftPurpose = useCallback((id, checked) => {
    setDraft((current) => ({ ...current, [id]: checked }))
  }, [])

  const value = useMemo(
    () => ({
      consent,
      purposes: consent ? consent.purposes : DEFAULT_PURPOSES,
      needsChoice: consent === null,
      isPanelOpen,
      draft,
      setDraftPurpose,
      setConsent,
      openPanel,
      closePanel,
    }),
    [consent, isPanelOpen, draft, setDraftPurpose, setConsent, openPanel, closePanel],
  )

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
}

export function useConsent() {
  const context = useContext(ConsentContext)
  if (!context) throw new Error('useConsent doit être appelé à l’intérieur d’un <ConsentProvider>')
  return context
}
